import { Immutable } from "immer";
import React, { JSX, useMemo } from "react";
import {
    isoProductID,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";

import { reverse, templates } from "@reactivated";

import { constants } from "@reactivated/constants";

import { IProduct } from "../../../models/catalogue";
import { check } from "../../../models/utils";
import { usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
import {
    Configurator,
    type IProps as IConfiguratorProps,
} from "../Configurator";
import { UpsellModal, extractUpsellBlock } from "../UpsellModal";
import {
    IModularConfiguratorOptionSet,
    IUpsellModalComponentFn,
    ModularConfiguratorOptionSet,
} from "../models";

const AppSlug = constants["tsicommon.types.AppSlug"];

export interface IPageData {
    title?: string;
    configurator_title?: string;
    preheader?: string;
    subhead?: string;
    description?: string;
    live_chat_header?: string;
    shipping_description?: string;
    show_shipping_lead?: boolean;
    show_financing_modal?: boolean;
    show_more_ways?: boolean;
    learn_more_target?: string;
    variant_configurator_description_data?: ReadonlyArray<{
        product: number;
        description?: string;
        preorder_shipping_description?: string;
    }>;
    phone_number?: string;
    design?: string;
    upsell_modal?: unknown;
    box_header?: string;
    top_button_text?: string;
    top_badge?: string;
    promo_copy?: string;
}

type IProps = Immutable<
    templates.ModularConfiguratorTemplate & {
        siteProps?: Partial<IConfiguratorProps>;
        buildAdditionalContent?: (
            product: IProduct | null,
            optionSet: IModularConfiguratorOptionSet,
            pageData: IPageData,
        ) => JSX.Element | null;
    }
>;

export const ModularConfigurator = (props: IProps) => {
    const optionSet: IModularConfiguratorOptionSet = check(
        ModularConfiguratorOptionSet.decode(
            JSON.parse(props.configurator_init_data),
        ),
    );

    const pageData: IPageData = JSON.parse(props.page_data);

    const financeLink = usePageURL("finance_link");
    const homeLink = usePageURL("home");
    const financingLink = financeLink || homeLink;
    const appSlug = usePageSetting("app_slug");

    const blockData = useMemo(
        () => extractUpsellBlock(props.bundle_modal_page),
        [props.bundle_modal_page],
    );

    // Build a stable component function for the upsell modal. This must be
    // memoized because it's used as a React component — if the identity changes
    // between renders, React unmounts/remounts the modal and loses its state.
    const upsellModalComponentFn = useMemo<IUpsellModalComponentFn | null>(
        () =>
            blockData
                ? (modalProps) => (
                      <UpsellModal {...modalProps} blockData={blockData} />
                  )
                : null,
        [blockData],
    );

    const showUpsellModal = useMemo(
        () => () => blockData !== null,
        [blockData],
    );

    const getUpsellModalComponentFn = useMemo(
        () => () => upsellModalComponentFn,
        [upsellModalComponentFn],
    );

    const buildAdditionalContent = props.buildAdditionalContent
        ? (
              product: IProduct | null,
              optionSet: IModularConfiguratorOptionSet,
          ) => props.buildAdditionalContent!(product, optionSet, pageData)
        : undefined;

    const getDeliveryCopy = (
        rootProduct: IProduct,
        isPreorder: boolean,
    ): string | null => {
        if (isPreorder && pageData.variant_configurator_description_data) {
            const productId = isoProductID.unwrap(rootProduct.id);
            const desc = pageData.variant_configurator_description_data.find(
                (d) => d.product === productId,
            );
            if (desc?.preorder_shipping_description) {
                return desc.preorder_shipping_description;
            }
        }
        return pageData.shipping_description || null;
    };

    const getDeliveryIcon = () => {
        const icon = optionSet.layout.shipping_callout_icon;
        return icon ? icon.url : null;
    };

    return (
        <Configurator
            optionSet={optionSet}
            financingLink={financingLink}
            basketLink={isoWebPageURL.wrap(reverse("basket:summary"))}
            configureGiftsLink={isoWebPageURL.wrap(
                reverse("basket:configure-gifts"),
            )}
            title={pageData.configurator_title ?? pageData.title}
            preheader={pageData.preheader}
            subhead={pageData.subhead}
            description={pageData.description}
            descriptions={pageData.variant_configurator_description_data?.map(
                (d) => ({
                    ...d,
                    product: isoProductID.wrap(d.product),
                }),
            )}
            liveChatHeader={pageData.live_chat_header}
            showShippingLead={pageData.show_shipping_lead}
            showFinancingModal={pageData.show_financing_modal}
            learnMoreSelector={pageData.learn_more_target}
            getDeliveryCopy={getDeliveryCopy}
            getDeliveryIcon={getDeliveryIcon}
            enableHistoryInteraction={true}
            showStarRatingsModal={appSlug !== AppSlug.TEMPURCA}
            configType="pdp-hero"
            buildAdditionalContent={buildAdditionalContent}
            phoneNumber={pageData.phone_number}
            showUpsellModal={showUpsellModal}
            getUpsellModalComponentFn={getUpsellModalComponentFn}
            {...props.siteProps}
        />
    );
};
